import { useIntl } from "react-intl";
import {
  StyledAppProgressCircular,
  StyledRevCircularProgress,
  StyledRevCircularProgressContent,
  StyledRevCirProgressContentBottom,
  StyledRevenueCard,
} from "./index.styled";
import { CurrencyEnum } from "@lib/types/Transaction";
import { AllCurrencies } from "@lib/types/Billing";
import CoinPriceTextFormat from "../../Others/CoinPriceTextFormat";
import { useMemo } from "react";
import { green, red, yellow } from "@ant-design/colors";
import { Col, ProgressProps, Row, Typography } from "antd";
import { StyledFlex } from "../../Table/index.styled";
import { Grid } from "antd";
import { AppCardProps } from "@lib/components/AppComponents/AppCard";
import { ActionButton } from "../../Buttons";

const { useBreakpoint } = Grid;

type Props = {
  title: string;
  data: {
    total: number;
    paid: number;
    currency?: AllCurrencies;
  };
  extra?: string | JSX.Element;
  cardProps?: AppCardProps;
  refetch?: () => void;
  navigate?: () => void;
  loading?: boolean;
};

const Revenue = (props: Props) => {
  const { title, data, extra, cardProps, refetch, navigate, loading } = props;
  const { total = 0, paid = 0, currency = CurrencyEnum.USD } = data;
  const { messages } = useIntl();
  const percent = (Number(paid) / Number(total)) * 100 || 0;
  const percentColor: ProgressProps["strokeColor"] = useMemo(() => {
    if (percent >= 95) {
      return green[5];
    }
    return {
      "0%": red[6],
      "50%": yellow[5],
      "100%": green[5],
    };
  }, [percent]);

  const screens = useBreakpoint();
  const size = useMemo(() => {
    if (screens.xl) return 130;
    if (screens.lg) return 100;
    if (screens.sm) return 150;
    return 150;
  }, [screens]);

  return (
    <StyledRevenueCard
      title={title}
      heightFull
      extra={
        <StyledFlex style={{ gap: 10 }}>
          {extra}
          <>
            {navigate && (
              <Typography.Link onClick={() => navigate()}>
                {messages["common.viewAll"] as string}
              </Typography.Link>
            )}
          </>
          {refetch && (
            <ActionButton
              type="refresh"
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              loading={loading}
            />
          )}
        </StyledFlex>
      }
      {...cardProps}
    >
      <Row>
        <Col xs={12} md={24} lg={24} xxl={24}>
          <StyledRevCircularProgress>
            <StyledAppProgressCircular
              strokeColor={percentColor}
              percent={percent}
              strokeWidth={5}
              // width={140}
              size={size}
              format={() => (
                <StyledRevCircularProgressContent>
                  <CoinPriceTextFormat
                    amount={paid}
                    currency={currency}
                    showCurrencySymbol
                    hideDigit
                  />
                  <span>{messages["common.paid"] as string}</span>
                </StyledRevCircularProgressContent>
              )}
            />
          </StyledRevCircularProgress>
        </Col>
        <Col xs={12} md={24} lg={24} xxl={24}>
          <StyledRevCirProgressContentBottom>
            <StyledFlex style={{ gap: 10, justifyContent: "space-between" }}>
              <span>{messages["common.total"] as string}</span>

              <CoinPriceTextFormat
                amount={total}
                currency={currency}
                showCurrencySymbol
                hideDigit
              />
            </StyledFlex>
            <StyledFlex style={{ gap: 10, justifyContent: "space-between" }}>
              <span>{messages["common.pending"] as string}</span>

              <CoinPriceTextFormat
                amount={Number(total) - Number(paid) || 0}
                currency={currency}
                showCurrencySymbol
                hideDigit
              />
            </StyledFlex>
          </StyledRevCirProgressContentBottom>
        </Col>
      </Row>
    </StyledRevenueCard>
  );
};

export default Revenue;
